// The schematic's SVG layer: one path per edge, from the right edge of a card to the
// left edge of the next, drawn under the cards.
import { useId } from 'react'
import { connectorPath, type Edge, type NodeId } from '../../lib/profilesGraph'

/** A card's place in the schematic frame: left and right edges, vertical centre, height. */
export type Anchor = { l: number; r: number; y: number; h: number }

// Several edges at one card fan out a few pixels apart rather than meeting in a point.
function fan(edges: Edge[], anchors: Map<NodeId, Anchor>, end: 'from' | 'to'): Map<string, number> {
  const other = end === 'from' ? 'to' : 'from'
  const by = new Map<NodeId, Edge[]>()
  for (const e of edges) by.set(e[end], [...(by.get(e[end]) ?? []), e])
  const out = new Map<string, number>()
  for (const [node, list] of by) {
    const a = anchors.get(node)
    if (!a || list.length < 2) continue
    list.sort((x, y) => (anchors.get(x[other])?.y ?? 0) - (anchors.get(y[other])?.y ?? 0))
    const step = Math.min(8, (a.h * 0.6) / (list.length - 1))
    list.forEach((e, i) => out.set(e.id, (i - (list.length - 1) / 2) * step))
  }
  return out
}

type Props = { edges: Edge[]; anchors: Map<NodeId, Anchor>; width: number; height: number; lit: Set<string> | null }

export function Connectors({ edges, anchors, width, height, lit }: Props) {
  const arrow = `pf-arrow-${useId().replace(/[^\w-]/g, '')}`
  const drawn = edges.filter((e) => anchors.has(e.from) && anchors.has(e.to))
  const out = fan(drawn, anchors, 'from')
  const into = fan(drawn, anchors, 'to')
  return (
    <svg className="pf-edges" aria-hidden="true" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <marker id={arrow} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="8" markerHeight="8" markerUnits="userSpaceOnUse" orient="auto">
          <path className="pf-arrow" d="M0 0.5L7.5 4L0 7.5z" />
        </marker>
      </defs>
      {drawn.map((e) => {
        const a = anchors.get(e.from)!
        const b = anchors.get(e.to)!
        const d = connectorPath({ x: a.r, y: a.y + (out.get(e.id) ?? 0) }, { x: b.l - 2, y: b.y + (into.get(e.id) ?? 0) })
        return (
          <path
            key={e.id}
            className={`pf-edge pf-edge--${e.kind}`}
            d={d}
            markerEnd={`url(#${arrow})`}
            data-lit={lit?.has(e.id) || undefined}
            data-dim={(lit && !lit.has(e.id)) || undefined}
          />
        )
      })}
    </svg>
  )
}
